/**
 * Watches the age of the real device GNSS fix and publishes a health band the UI
 * can react to (grey the boat, show the fix age, warn when it's been lost a
 * while). It only runs against the real device feed: when the simulator is the
 * source we clear the health to null, so the training/sim position path is never
 * treated as stale.
 *
 * It ticks once a second because the fix age keeps growing even when nothing new
 * arrives, so we can't wait for a store change to notice a dropout. Nothing
 * renders; it just keeps the health up to date.
 */
import { useEffect } from 'react';
import { usePositionSourceStore } from '@/store/positionSource';
import { useGnssStore } from '@/store/gnss';
import { fixHealthForAge } from './fixHealth';

export function GnssHealthRunner() {
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);

  useEffect(() => {
    // Simulator (training) drives a continuous position; it is never stale, so
    // clear any health left over from a previous device session and do nothing.
    if (sourceKind !== 'device') {
      const st = usePositionSourceStore.getState();
      if (st.fixHealth !== null || st.fixAgeSec !== null) st.setFixHealth(null, null);
      return;
    }

    const tick = () => {
      const lastMs = useGnssStore.getState().lastFixWallMs;
      const set = usePositionSourceStore.getState().setFixHealth;
      if (lastMs == null) {
        // Device selected but no fix has ever arrived: treat as lost so the
        // warning explains the boat isn't placed yet.
        set('lost', null);
        return;
      }
      const ageMs = Date.now() - lastMs;
      set(fixHealthForAge(ageMs), Math.round(ageMs / 1000));
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [sourceKind]);

  return null;
}
